<template>
    <div>
        <table class="kgr-table kgr-table--striped table-groups kgr-row roles kgr-table--block">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('memberRole') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Start') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Ende') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('memberRole.department') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('memberRole.team') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline notice-cell">
                        {{ $t('Bemerkungen') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <template
                    v-for="role in roles"
                    :key="`row${role.id}`"
                >
                    <kgr-member-role-edit-row
                        :role="role"
                        :edit-roles="editRoles"
                        @remove="remove(role)"
                    />
                    <kgr-member-role-show-row
                        :role="role"
                        :edit-roles="editRoles"
                        @edit="edit(role)"
                        @remove="remove(role)"
                    />
                </template>

                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-member-role-select
                            v-model="newRole.parameterValue"
                            :club-id="clubId"
                            @change="updateRoles()"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <kgr-date-picker v-model="newRole.from" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <kgr-date-picker v-model="newRole.to" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-department-select
                            :key="`department${selectResetKey}`"
                            ref="departmentSelect"
                            v-model="newRole.department"
                            hide-label
                            :club-id="clubId"
                            exclude-if-basic-data-not-viewable
                            @post-input="onDepartmentChange"
                            @optionsLoaded="departmentOptionsLoaded"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-team-select
                            :key="`team${selectResetKey}`"
                            ref="teamSelect"
                            v-model="newRole.team"
                            hide-label
                            :department="newRole.department"
                            :club-id="clubId"
                            @post-input="onTeamChange"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label date-picker">
                            <textarea
                                v-model.trim="newRole.notice"
                                class="kgr-input"
                                rows="3"
                            ></textarea>
                        </div>
                    </td>
                    <td class="kgr-table__cell">
&nbsp;
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="showButton"
            class="button_save"
            :value="$t('saveChanges')"
            :disabled="!isSavable()"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script>
import DatePicker from '../../common/form/picker/DatePicker';
import Department from '../../../js/dto/Department';
import DepartmentRepository from '../../../js/repository/DepartmentRepository';
import DepartmentSelect from '../../common/form/select/DepartmentSelect.vue';
import KgrButton from '../../common/form/button/KgrButton';
import MemberRepository from '../../../js/repository/MemberRepository';
import MemberRole from '../../../js/dto/MemberRole';
import MemberRoleEditRow from './MemberRoleEditRow';
import MemberRoleSelect from './MemberRoleSelect';
import MemberRoleShowRow from './MemberRoleShowRow';
import Team from '../../../js/dto/Team';
import TeamRepository from '../../../js/repository/TeamRepository';
import TeamSelect from '../../common/form/select/TeamSelect.vue';

const HTTP_STATUS_CONFLICT = 409;

export default {
    components: {
        'kgr-department-select': DepartmentSelect,
        'kgr-team-select': TeamSelect,
        'kgr-date-picker': DatePicker,
        'kgr-member-role-select': MemberRoleSelect,
        'kgr-member-role-edit-row': MemberRoleEditRow,
        'kgr-member-role-show-row': MemberRoleShowRow,
        'kgr-button': KgrButton,
    },
    props: {
        roles: {
            type: Array,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            newRole: new MemberRole(),
            dataRoles: [],
            editRoles: [],
            loading: true,
            loadingDepartments: true,
            loadingTeams: true,
            teams: Array[Team],
            departments: Array[Department],
            pendingCreate: false,
            pendingUpdate: false,
            selectResetKey: 0,
        };
    },
    computed: {
        showButton() {
            return this.isCreatable || this.editRoles.length > 0;
        },
    },
    created() {
        if (this.newRole.department) {
            this.selected = this.newRole.department;
        }
        this.dataRoles = this.roles;
        this.updateDepartments();
        this.updateTeams();
    },
    methods: {
        isSavable() {
            if (this.editRoles.length === 0 && this.newRole.isEmpty()) {
                return false;
            }

            const hasInvalidEdits = this.editRoles.filter((editRole) => !editRole.isValid()).length > 0;
            if (hasInvalidEdits) {
                return false;
            }

            if (!this.newRole.isEmpty() && !this.newRole.isValid()) {
                return false;
            }

            return true;
        },
        remove(role) {
            showConfirm(this.$t('confirm'), this.$t('memberRole.confirm.delete', { name: role.parameterValue.value }), () => {
                this.editRoles = this.editRoles.filter((r) => r.id !== role.id);
                MemberRepository
                    .removeMemberRole(this.memberId, role.id)
                    .then(() => {
                        this.$notify.success('memberRole.delete.success', { name: role.parameterValue.value });
                        this.emitUpdated();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(role) {
            this.editRoles.push(role);
        },
        save() {
            if (this.editRoles.length > 0) {
                this.update();
            }

            if (!this.newRole.isEmpty()) {
                this.create();
            }
        },
        create() {
            if (this.newRole.department && !this.newRole.department.id) {
                this.newRole.department = null;
            }
            if (this.newRole.team && !this.newRole.team.id) {
                this.newRole.team = null;
            }
            if (this.checkOverlappingRole(this.newRole)) {
                this.$notify.error('Diese Funktion gibt es in der Kombination mit Abteilung und/oder Team bereits in einem sich überschneidenden Zeitraum.', { name: this.newRole.parameterValue.value });
                return;
            }

            this.pendingCreate = true;
            MemberRepository
                .createMemberRole(this.memberId, this.newRole)
                .then(() => {
                    this.newRole = new MemberRole();
                    this.selectResetKey++;
                    this.updateTeams();
                    this.updateDepartments();
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch((error) => {
                    if (error.status === HTTP_STATUS_CONFLICT) {
                        this.$notify.error('Diese Funktion gibt es in der Kombination mit Abteilung und/oder Team bereits in einem sich überschneidenden Zeitraum.', { name: this.newRole.parameterValue.value });
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
                .finally(() => {
                    this.pendingCreate = false;
                });
        },
        checkOverlappingRole(role) {
            let overlappingRoles = false;
            this.dataRoles.forEach((dataRole) => {
                if (role.id !== dataRole.id && role.parameterValue.value === dataRole.parameterValue.value) {
                    const overlaps = role.overlaps(dataRole);
                    overlappingRoles = overlappingRoles || overlaps;
                }
            });
            return overlappingRoles;
        },
        update() {
            let overlappingRole = null;
            this.editRoles.forEach((role) => {
                overlappingRole = this.checkOverlappingRole(role) ? role : overlappingRole;
            });
            if (overlappingRole) {
                this.$notify.error('Diese Funktion gibt es in der Kombination mit Abteilung und/oder Team bereits in einem sich überschneidenden Zeitraum.', { name: overlappingRole.parameterValue.value });
                return;
            }

            this.pendingUpdate = true;
            MemberRepository
                .updateMemberRoles(this.memberId, this.editRoles)
                .then(() => {
                    this.editRoles = [];
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pendingUpdate = false);
        },
        emitUpdated() {
            this.$emit('updated');
        },
        updateRoles() {
            this.dataRoles = MemberRoleSelect.data().options;
        },
        updateDepartments() {
            this.loadingDepartments = true;
            if (this.newRole.team) {
                this.newRole.department = this.getDepartmentById(this.newRole.team.departmentId);
                this.reloadPicker();
            } else {
                DepartmentRepository.findAll(this.clubId)
                    .then((departments) => {
                        this.departments = departments.filter((department) => department.isViewable);
                        this.loadingDepartments = false;
                    })
                    .catch(() => this.$notify.error('error.loadingData'));
            }
        },
        onDepartmentChange(department) {
            this.newRole.department = department;
            if (this.newRole.team && department && this.newRole.team.departmentId === department.id) {
                return;
            }
            this.updateTeams();
        },
        onTeamChange(team) {
            this.newRole.team = team;
            this.updateDepartments();
        },
        departmentOptionsLoaded($event) {
            this.departments = $event;
            this.loading = false;
        },
        updateTeams() {
            this.newRole.team = null;
            if (this.newRole.department) {
                this.loadingTeams = true;
                DepartmentRepository.findTeams(this.newRole.department.id)
                    .then((teams) => {
                        this.teams = teams;
                    })
                    .catch(() => this.$notify.error('error.loadingData'))
                    .finally(() => this.loadingTeams = false);
            } else {
                this.loadingTeams = true;
                TeamRepository.findAll(this.clubId)
                    .then((teams) => {
                        this.teams = teams;
                    })
                    .catch(() => this.$notify.error('error.loadingData'))
                    .finally(() => this.loadingTeams = false);
            }
        },
        getDepartmentById(id) {
            if (!Array.isArray(this.departments)) {
                return null;
            }
            const department = this.departments.find((department) => department.id === id);
            if (!department) {
                return null;
            }
            return department;
        },
        reloadPicker() {
            this.$refs.departmentSelect.reload();
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .button_save {
    display: block;
    margin-left: auto;
}

div .date-picker {
    border: 1px solid transparent;

    &.invalid {
        border: 1px solid #f44336;
    }
}

.kgr-table__cell {
    vertical-align: top;
}

.notice-cell {
    width: 30%;
}
</style>
